<script>
  import template from './template.html';
  import ClickOutside from 'vue-click-outside';
  import draggable from 'vuedraggable'
  import {mapActions, mapGetters} from 'vuex';
  import {uid} from 'uid';
  import './style.scss';
  import {clone, isEmpty, isEqual} from 'lodash';

  import {
    FormatColumnsIcon,
    ArrowUpDownIcon,
    CloseIcon,
  } from '../../../icons.js';

  const HIDE_SHOW_NAMESPACE = 'hidenshow_v1';

  export default {
    props: {
      initColumns: {
        required: true,
        type: Array,
      },

      /**
       * The name of the datatable.
       * Used to save user preferences.
       */
      prefName: {
        type: String
      },

      savedColumns: {
        type: Array,
        default: function () {
          return [];
        }
      }
    },

    template,

    beforeMount () {
      this.initialColumns = clone(this.initColumns);
      let cleanSavedColumns = this.cleanSavedColumns;

      let savedColumns = !isEmpty(cleanSavedColumns) ? cleanSavedColumns : undefined;

      if (savedColumns) {
        //parse saved columns
        savedColumns = this._parseSavedColumns(savedColumns, this.initialColumns);
      }

      this.init(this.initialColumns, savedColumns);
    },

    methods: {
      ...mapActions('AcclaroSearchStore', [
        'addHiddenColumn',
        'removeHiddenColumn',
        'reorderColumns',
        'updateColumns',
      ]),

      /**
       * Parse saved columns array into columns object
       *
       * @param Array savedColumns [{id, visible, ...}]
       * @param Array initialColumns ['ID', 'Plunet', ...]
       * @return Object {Id: true, Comments: false, Name: true, ...}
       */
      _parseSavedColumns (savedColumns, initialColumns) {
        let columns = {};

        savedColumns.forEach(col => columns[col.id] = col.visible);

        return columns;
      },

      /**
       * set initial state of columns
       *
       * @param Array initColumns
       * @param Array savedColumns
       */
      init (initColumns, savedColumns) {
        if (savedColumns) {
          this.columns = savedColumns;
        }

        //get all of the datatable columns
        //set them to visible/hidden
        initColumns.forEach(col => {

          //don't do anything if the columns visiblility state
          //was previously set
          if (savedColumns && this.columns.hasOwnProperty(col)) return;

          this.columns[col] = true;
        })

        //add hidden columns to list
        let hiddenColumns = this.setHiddenColumns(initColumns);

        //add hidden columns to vuex
        hiddenColumns.forEach(col => this.addHiddenColumn(col.id));

        if (savedColumns) {
          //reorder datatable columns by saved state
          this.reorderColumns(this.cleanSavedColumns);
        }

        //add visible columns to visible columns list
        this.setVisibleColumns(this.getColumns);
      },

      /**
       * Build an object array of Hidden columns
       *
       * @param Array columns
       * @return Array
       */
      setHiddenColumns (columns) {
        //add visible columns to visible columns list
        return this.hiddenList = this._buildColumns(columns, false);
      },

      /**
       * Build an object array of visible columns
       *
       * @param Array columns
       * @return Array
       */
      setVisibleColumns (columns) {
        //add visible columns to visible columns list
        return this.visibleList = this._buildColumns(columns, true);
      },

      _buildColumns (columns, visible) {
        return columns.map(col => {
          return { name: col, id: col, uid: uid() };
        })
        .filter(col => this.columns[col.id] === visible);
      },

      /**
       * Open/close the modal
       *
       * @param Boolean state
       * @return Boolean
       */
      toggleModal (state) {
        if (typeof state == 'boolean') return this.modalIsVisible = state;

        return this.modalIsVisible = !this.modalIsVisible;
      },

      /**
       * When the user drags a column item
       *
       * @param Event e
       * @return Void
       */
      dragged (e) {
        let {to, from, newIndex, oldIndex} = e;
        let { visibleList, hiddenList } = this;
        let inVisibleCol = $(to).is('.visible');

        const column = inVisibleCol
          ? visibleList[newIndex]
          : hiddenList[newIndex];

        if (inVisibleCol) {
          this.removeHiddenColumn(column.id);
        } else {
          this.addHiddenColumn(column.id);
        }

        this.updateColumnVisibility(column, inVisibleCol);
        this.reorderColumns(this.visibleList);

        this.$forceUpdate()
      },

      /**
       * Set a columns object to hidden/visible
       *
       * @param Object item
       * @param Boolean isVisible
       * @return Boolean
       */
      updateColumnVisibility (item, isVisible) {
        return this.columns[item.id] = isVisible;
      },

      /**
       * Update a columns state (hidden/visible)
       * when a user clicks a checkbox
       *
       * @param Object column
       * @param Integer currentIndex
       * @return void
       */
      swapColumn (column, currentIndex) {
        let inVisibleCol = this.columns[column.id];
        let {visibleList, hiddenList} = this;

        function pushRemove (pushToList, removeFromList) {
          pushToList.push(column);
          removeFromList.splice(currentIndex, 1);
        }

        if (inVisibleCol) {
          pushRemove(visibleList, hiddenList);
          this.removeHiddenColumn(column.id);
          this.reorderColumns(this.visibleList);
        } else {
          pushRemove(hiddenList, visibleList);
          this.addHiddenColumn(column.id);
        }
      },


      /**
       * Reset columns back to default
       *
       * @param Object column
       * @param Integer currentIndex
       * @return void
       */
      resetColumns () {
        //remove all hidden columns from vuex
        this.hiddenColumns.forEach(col => {
          this.removeHiddenColumn(col);
        })

        //set columns array back to it's initial state in vuex
        //@NOTE this will notify acclaroDatatable to update columns order
        this.updateColumns(this.initialColumns);

        //reset hidden and visible columns
        this.visibleList.length = 0;
        this.hiddenList.length = 0;

        //re initiate state
        this.init(this.initialColumns);

        //remove all hidden columns from vuex
        this.hiddenColumns.forEach(col => {
          this.removeHiddenColumn(col);
        })

        this.$forceUpdate();
      },

      /**
       * save column changes
       *
       * @TODO - move to AcclaroDatatable component
       *
       * @param Object column
       * @param Integer currentIndex
       * @return void
       */
      saveColumns () {
        let { prefName, columns, visibleList } = this;

        if (!prefName) {
          this.toggleModal(false);
          return console.error('no datatable prefName given')
        }

        let sortedColumns = Object.assign({}, columns);

        sortedColumns = Object
          .keys(sortedColumns)
          //create an array from columns object
          .map(col => {
            return {id: col, visible: sortedColumns[col]}
          })
          //sort columns object by visible columns list
          .sort(function (a, b) {
            return visibleList.findIndex(ele => ele.id == a.id) - visibleList.findIndex(ele => ele.id == b.id)
          });

        const val = JSON.stringify({ columns: sortedColumns });

        let params = {
          type: HIDE_SHOW_NAMESPACE,
          key: prefName,
          val
        };

        //set user preferences`
        this.setUserPreference(params).then(result => {

          //close modal
          this.toggleModal(false);
        })
      },

      /**
       * Set a user preference
       *
       * @param Object params = {type, key, val}
       * @return Promise
       */
      setUserPreference (params = {}) {
        let {type, key, val} = params;

        return axios.post(`/users/preference/${type}/${key}`, {
          value: val
        });
      },

      /**
       * get a user preference
       *
       * @param Object opts = {type, key}
       * @return Promise
       */
      getUserPreference (opts = {}) {
        let {type,key} = opts;

        return axios.get(`/users/preference/${type}/${key}`);
      },

      /**
       * Cancel column changes
       *
       * @param Object column
       * @param Integer currentIndex
       * @return void
       */
      cancelColumnEdit () {
        //@TODO

        this.toggleModal();
      },

      log () {},

      checkMove (e) {},

      dragging (e) {},
    },

    computed: {

      ...mapGetters('AcclaroSearchStore', [
        'hiddenColumns',
        'getColumns',
      ]),

      /**
       * Check if user has edited the columns
       *
       * @return Boolean
       */
      isEdited () {
        const columns = this.visibleList.map(ele => ele.id);

        return !isEqual(this.initialColumns, columns);
      },

      /**
       * return a clean version of the savedColumns
       *
       * @description -returns a savedColumns array without any columns that aren't defined in
       * the initialColumns array.  columns removed.  this might happen if the user saves their
       * column order and then we remove a column in a future release
       *
       * @return Array|undefined [{id: 'ID', visible: true}]
       */
      cleanSavedColumns () {
        let savedColumns = this.savedColumns;
        let initialColumns = this.initialColumns;

        if(isEmpty(savedColumns)) return [];

        //create a new copy
        savedColumns = clone(savedColumns);

        //filter out any columns that aren't in the initial columns
        let result = savedColumns.filter(columnObject => {
          //columnObect{id: 'Plunet', visible: true}
          return initialColumns.includes(columnObject.id);
        })

        //get all the result column names
        let resultColumnNames = result.map((columnObject) => columnObject.id);

        //get any columns missing from saved columns
        let additionalColumns = initialColumns.filter(columnName => {
          return !resultColumnNames.includes(columnName)
        });

        //if any columns are missing from our result
        if (additionalColumns.length) {
          additionalColumns.forEach(columnName => {
            let indx = initialColumns.findIndex(col => col == columnName);

            //add missing column to our result in the correct index
            result.splice(indx, 0, {id: columnName, visible: true});
          });
        }

        return result;
      },
    },

    data () {
      return {
        modalIsVisible: false,
        initialColumns: [],
        $dirty: false,
        columns: {
          /*mike: true,
          ashley: false,
          jessica: false,*/
        },
        visibleList: [],
        hiddenList: [
          /*{ name: "Ashley", id: 'ashley' },
          { name: "Mike", id: 'Mike' },
          { name: "Eva", id: 'eva' }*/
        ]
      }
    },

    components: {
      FormatColumnsIcon,
      ArrowUpDownIcon,
      CloseIcon,
    },

    directives: {
      ClickOutside,
      draggable,
    },
  };
</script>
